import React from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    LabelList
} from "recharts";

const AdvancedInvestmentCharts = ({ indicadores, indicadoresVacacional }) => {
    if (!indicadores || Object.keys(indicadores).length === 0) {
        return <div className="text-gray-500">Cargando indicadores...</div>;
    }

    const alquilerNormal = indicadores.alquiler || [];
    // Extraer y validar alquiler vacacional
    const alquilerVacacional =
        Array.isArray(indicadoresVacacional) && indicadoresVacacional.some(
            item => item.alquiler?.precioMensualEstimado !== null
        )
            ? indicadoresVacacional.map(item => ({
                ...item,
                tipoReforma: {
                    ...item.tipoReforma,
                    nombre: `${item.tipoReforma?.nombre || "Reforma"} (Vacacional)`
                }
            }))
            : [];

    // Combinar solo si hay datos válidos
    const alquilerCombinado = [
        ...alquilerNormal,
        ...alquilerVacacional.filter(item => item.alquiler?.precioMensualEstimado !== null)
    ];

    const escenariosUnicos = Array.from(
        new Set(indicadores.venta.map(i => i.tipoReforma?.nombre || "Otro"))
    );

    const roiPeriods = [1, 3, 5, 10];
    const roiData = roiPeriods.map((year) => {
        const row = { year: `${year} años` };
        indicadores.venta.forEach((item) => {
            const label = item.tipoReforma?.nombre || "Otro";
            const roiEntry = item.venta?.roi?.find(r => r.year === year);
            row[label] = roiEntry ? parseFloat(roiEntry.roi) : null;
        });
        return row;
    });

    const getUniqueScenarioData = (dataArray, key, valueKey, outputKey) => {
        const seen = new Set();
        return dataArray.filter(item => {
            const escenario = item.tipoReforma?.nombre || "Otro";
            if (!seen.has(escenario)) {
                seen.add(escenario);
                return true;
            }
            return false;
        }).map(item => ({
            escenario: item.tipoReforma?.nombre || "Otro",
            [outputKey]: item[key]?.[valueKey] ? parseFloat(item[key][valueKey]) : null
        }));
    };

    const rentabilidadAlquilerData = getUniqueScenarioData(alquilerCombinado, "alquiler", "rentabilidadAnual", "rentabilidad");
    const retornoData = getUniqueScenarioData(alquilerCombinado, "alquiler", "tiempoRetornoMeses", "retorno");
    const precioVentaData = getUniqueScenarioData(indicadores.venta, "venta", "precioEstimado", "precio");
    const precioAlquilerData = getUniqueScenarioData(alquilerCombinado, "alquiler", "precioMensualEstimado", "alquiler");

    const costeReformaData = getUniqueScenarioData(indicadores.venta, "venta", null, "");
    costeReformaData.forEach((item) => {
        const matching = indicadores.venta.find(i => i.tipoReforma?.nombre === item.escenario);
        item.coste = matching ? parseFloat(matching.costeReforma) : null;
    });

    return (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <div className="bg-blue-50 p-4 shadow rounded border border-blue-200">
                <h3 className="text-lg font-bold text-blue-800 mb-2">📈 ROI venta por periodo</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={roiData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="year" />
                        <YAxis unit="%" />
                        <Tooltip />
                        <Legend />
                        {escenariosUnicos.map((escenario, index) => (
                            <Line
                                key={escenario}
                                type="monotone"
                                dataKey={escenario}
                                stroke={["#8884d8", "#82ca9d", "#ff8042", "#ffbb28", "#a78bfa"][index]}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>

            <div className="bg-green-50 p-4 shadow rounded border border-green-200">
                <h3 className="text-lg font-bold text-green-800 mb-2">📊 Rentabilidad anual alquiler</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={rentabilidadAlquilerData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="escenario" angle={-30} textAnchor="end" interval={0} height={100} style={{ fontSize: 11 }} />
                        <YAxis unit="%" />
                        <Tooltip />
                        <Bar dataKey="rentabilidad" fill="#82ca9d">
                            <LabelList dataKey="rentabilidad" position="top" />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="bg-purple-50 p-4 shadow rounded border border-purple-200">
                <h3 className="text-lg font-bold text-purple-800 mb-2">⏳ Tiempo retorno inversión (meses)</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={retornoData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="escenario" angle={-30} textAnchor="end" interval={0} height={100} style={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="retorno" fill="#8884d8">
                            <LabelList dataKey="retorno" position="top" />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="bg-yellow-50 p-4 shadow rounded border border-yellow-200">
                <h3 className="text-lg font-bold text-yellow-800 mb-2">💶 Precio venta estimado</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={precioVentaData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="escenario" angle={-30} textAnchor="end" interval={0} height={100} style={{ fontSize: 11 }} />
                        <YAxis unit="€" />
                        <Tooltip />
                        <Bar dataKey="precio" fill="#ffc658">
                            <LabelList dataKey="precio" position="top" />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="bg-orange-50 p-4 shadow rounded border border-orange-200">
                <h3 className="text-lg font-bold text-orange-800 mb-2">🏠 Precio alquiler mensual estimado</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={precioAlquilerData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="escenario" angle={-30} textAnchor="end" interval={0} height={100} style={{ fontSize: 11 }} />
                        <YAxis unit="€" />
                        <Tooltip />
                        <Bar dataKey="alquiler" fill="#ff8042">
                            <LabelList dataKey="alquiler" position="top" />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="bg-red-50 p-4 shadow rounded border border-red-200">
                <h3 className="text-lg font-bold text-red-800 mb-2">💸 Coste estimado de reforma</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={costeReformaData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="escenario" angle={-30} textAnchor="end" interval={0} height={100} style={{ fontSize: 11 }} />
                        <YAxis unit="€" />
                        <Tooltip />
                        <Bar dataKey="coste" fill="#f87171">
                            <LabelList dataKey="coste" position="top" />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default AdvancedInvestmentCharts;